/**
 * FHIR Validation - UI Presentation Model
 *
 * Adapts conductor's {@link FHIRValidationResult} into a shape optimised for display in validation/error panels.
 */
import type { FHIRValidationResult } from './fhir.model';

/** Severity levels used in UI display. */
export type ValidationSeverity = 'error' | 'warning' | 'info';

/** Validation result adapted for UI presentation. */
export interface FhirValidationResult {
  readonly isValid: boolean;
  readonly issues: FhirValidationIssue[];
  readonly counts: { errors: number; warnings: number; info: number };
  readonly timestamp: Date;
}

/** A single validation issue displayed in an error panel. */
export interface FhirValidationIssue {
  readonly severity: ValidationSeverity;
  readonly message: string;
  readonly path: string | null;
  readonly lineNumber: number | null;
  readonly code: string;
}

/** Convert conductor's {@link FHIRValidationResult} to the UI model. */
export function toFhirValidationResult(r: FHIRValidationResult): FhirValidationResult {
  const issues: FhirValidationIssue[] = [
    ...r.errors.map((e) => ({
      severity: 'error' as const,
      message: e.message,
      path: e.location || null,
      lineNumber: null,
      code: e.code ?? 'FHIR_ERROR',
    })),
    ...r.warnings.map((w) => ({
      severity: 'warning' as const,
      message: w.message,
      path: w.location || null,
      lineNumber: null,
      code: w.code ?? 'FHIR_WARNING',
    })),
  ];

  return {
    isValid: r.valid,
    issues,
    counts: { errors: r.errors.length, warnings: r.warnings.length, info: 0 },
    timestamp: new Date(),
  };
}

/** Empty (all-valid) result for initial state. */
export function createEmptyValidationResult(): FhirValidationResult {
  return { isValid: true, issues: [], counts: { errors: 0, warnings: 0, info: 0 }, timestamp: new Date() };
}
